import { useEffect, useRef, useState } from 'react';
import type { ReportReason, Thread, ThreadMessage } from '@sfc/sdk';
import { PlatformApiError } from '@sfc/sdk';
import { platform } from '../api/client';
import { ReportPanel } from '../safety/ReportPanel';

/**
 * One conversation.
 *
 * The composer is the whole screen's point, so what it can and cannot do is
 * said in the answer rather than worked out here: the server returns whether
 * this person may write and why not, and this draws that. A client that
 * decided for itself would be a second copy of a rule that is going to change
 * the day messaging is charged for.
 */
export function ThreadScreen({
  matchId,
  onBack,
  onChanged,
}: {
  matchId: string;
  onBack: () => void;
  onChanged: () => void;
}): React.JSX.Element {
  const [thread, setThread] = useState<Thread>();
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [reporting, setReporting] = useState(false);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const loaded = await platform.match.thread(matchId);
        if (!live) return;
        setThread(loaded);
        // Looked at, not merely fetched: said once the conversation is on
        // screen, which is the only moment it is true.
        await platform.match.markRead(matchId).catch(() => undefined);
        if (live) onChanged();
      } catch {
        if (live) setFailed(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [matchId, onChanged]);

  // The end of a conversation is the part somebody wants to be looking at.
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [thread?.messages.length]);

  async function send() {
    const body = draft.trim();
    if (!body || sending || !thread) return;
    setSending(true);
    setProblem(undefined);

    try {
      const sent = await platform.match.sendMessage(matchId, body);
      setDraft('');
      setThread({ ...thread, messages: [...thread.messages, sent] });
      onChanged();
    } catch (error) {
      setProblem(
        error instanceof PlatformApiError
          ? error.message
          : 'That did not send. Try again in a moment.',
      );
    } finally {
      setSending(false);
    }
  }

  if (failed) {
    return (
      <div className="placeholder">
        <h2>We cannot open this conversation</h2>
        <button className="button" type="button" onClick={onBack}>
          Back to your matches
        </button>
      </div>
    );
  }

  if (!thread) {
    return (
      <p className="loading" data-testid="thread-loading">
        Opening…
      </p>
    );
  }

  if (reporting) {
    return (
      <ReportPanel
        subject={thread.withName}
        onReport={(reason: ReportReason, detail: string) =>
          platform.match.reportPerson(matchId, reason, detail || undefined)
        }
        onBlock={() => platform.match.blockMatch(matchId)}
        onClose={() => {
          setReporting(false);
          // Blocking closes the thread, so the list is where to come back to
          // rather than a conversation that can no longer be had.
          onChanged();
          onBack();
        }}
      />
    );
  }

  return (
    <section className="thread" aria-label={`Conversation with ${thread.withName}`}>
      {/*
        One line for who, one for what it is about. At 390px the three-column
        version wrapped the idea's title under the back button and pushed
        everything sideways, which is the sort of thing that only shows up on
        a phone.
      */}
      <header className="thread__head">
        <div className="thread__row">
          <button
            className="button button--quiet thread__back"
            type="button"
            onClick={onBack}
            data-testid="thread-back"
            aria-label="Back to your matches"
          >
            ←
          </button>
          <h2 className="thread__who" data-testid="thread-who">
            {thread.withName}
          </h2>
          <button
            className="button button--quiet"
            type="button"
            data-testid="report-person"
            onClick={() => setReporting(true)}
          >
            Report
          </button>
        </div>
        <p className="fine thread__about">{thread.ideaTitle}</p>
      </header>

      <ol className="messages" data-testid="messages">
        {thread.messages.length === 0 && (
          <li className="fine messages__empty" data-testid="thread-empty">
            Nothing said yet. You both agreed to talk, so somebody has to go first.
          </li>
        )}
        {thread.messages.map((message: ThreadMessage, index: number) => (
          <li
            key={message.id}
            className={[
              'message',
              message.fromMe ? 'message--mine' : '',
              // Consecutive lines from one person are one block. The corner
              // only rounds where a run begins and ends.
              continues(thread.messages, index) ? 'message--run' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {/* An erased body is an empty one: the row stays so the
                conversation still reads as one, and the gap is named rather
                than left as a blank bubble nobody can explain. */}
            {message.body ? (
              <p className="message__body">{message.body}</p>
            ) : (
              <p className="message__body message__body--gone">This message is no longer kept.</p>
            )}
            {/*
              Only where it tells somebody something. Three lines a minute
              apart, each stamped with the same time, is noise -- so the stamp
              appears at the end of a run and whenever there has been a real
              gap in the conversation.
            */}
            {endsRun(thread.messages, index) && (
              <time className="message__at" dateTime={message.sentAt}>
                {when(message.sentAt)}
              </time>
            )}
          </li>
        ))}
        <div ref={end} />
      </ol>

      {problem && (
        <p className="notice notice--problem" role="status" data-testid="send-problem">
          {problem}
        </p>
      )}

      {thread.canSend ? (
        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            void send();
          }}
        >
          <label className="sr-only" htmlFor="message">
            Write a message
          </label>
          <textarea
            id="message"
            className="composer__box"
            rows={2}
            maxLength={2000}
            value={draft}
            placeholder={`Message ${thread.withName}`}
            data-testid="composer"
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks a line. On a phone the button
              // is under the thumb anyway, so nothing depends on this.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <button
            className="button button--primary"
            type="submit"
            disabled={sending || draft.trim().length === 0}
            data-testid="send"
          >
            Send
          </button>
        </form>
      ) : (
        <p className="notice notice--problem" role="status" data-testid="cannot-send">
          {thread.cannotSendBecause ?? 'You cannot write here.'}
        </p>
      )}
    </section>
  );
}

/** Ten minutes: long enough that a reader wonders when, short enough to group. */
const GAP = 10 * 60 * 1000;

const sameRun = (a: ThreadMessage, b: ThreadMessage): boolean =>
  a.fromMe === b.fromMe && Math.abs(Date.parse(b.sentAt) - Date.parse(a.sentAt)) < GAP;

const continues = (all: ThreadMessage[], index: number): boolean =>
  index > 0 && sameRun(all[index - 1]!, all[index]!);

const endsRun = (all: ThreadMessage[], index: number): boolean =>
  index === all.length - 1 || !sameRun(all[index]!, all[index + 1]!);

/** Today shows a time; anything older shows the day as well. */
function when(iso: string): string {
  const at = new Date(iso);
  const today = new Date().toDateString() === at.toDateString();
  return at.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    ...(today ? {} : { day: 'numeric', month: 'short' }),
  });
}
